import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ErrorIcon from '@mui/icons-material/Error';
import EventIcon from '@mui/icons-material/Event';
import PersonIcon from '@mui/icons-material/Person';
import SendToMobileIcon from '@mui/icons-material/SendToMobile';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, ListItem, Menu, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { ListChildComponentProps } from 'react-window';

import { CustomerUserDetails, ShortCoachSession } from '../../../backendsdk';
import {
    TrackedListItemButton as ListItemButton,
    TrackedMenuItem as MenuItem,
} from '../../../components/TrackedComponents';
import useProfile from '../../../hooks/profile';
import palette from '../../ColorPalette';
import { BEHAVIOR_MAP } from './SessionDetails';

interface ListRowData {
    sessions: ShortCoachSession[];
    selectedSessionId?: string;
    users: CustomerUserDetails[];
    setSessionIdToDelete: CallableFunction;
    canRemoveSession: boolean;
}

const SessionListRow: React.FC<ListChildComponentProps<ListRowData>> = (props) => {
    const { index, style } = props;
    const session = props.data.sessions[index];
    const [contextMenu, setContextMenu] = useState<{
        mouseX: number;
        mouseY: number;
    } | null>(null);
    const history = useHistory();
    const { t } = useTranslation();
    const { profile } = useProfile();

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        setContextMenu(
            contextMenu === null
                ? {
                      mouseX: event.clientX,
                      mouseY: event.clientY,
                  }
                : null,
        );
    };

    const handleClose = () => {
        setContextMenu(null);
    };

    const eventCount = session.agenda?.filter((item) => item.type === 'event').length || 0;
    let value;
    if (eventCount > 0) {
        value = eventCount === 1 ? t('content.fleet.event') : t('content.fleet.events', { count: eventCount });
    } else {
        value = t('content.fleet.no_events');
    }
    const eventsField = (
        <Box sx={{ display: 'flex' }}>
            <Box sx={{ width: 28, display: 'flex', alignItems: 'center' }}>
                <WarningIcon
                    fontSize="small"
                    sx={{ color: eventCount === 0 ? 'wellnessGray.main' : palette.red[700] }}
                />
            </Box>
            <Typography
                data-testid={`session-${session.session_id}-events-${eventCount}`}
                sx={{ color: eventCount === 0 ? 'wellnessGray.main' : palette.red[700] }}
            >
                {value}
            </Typography>
        </Box>
    );

    let datetimeField;
    if (session.completed_at) {
        datetimeField = (
            <Box sx={{ display: 'flex' }}>
                <Box sx={{ width: 28, display: 'flex', alignItems: 'center' }}>
                    <EventIcon fontSize="small" sx={{ color: 'primary.main' }} />
                </Box>
                <Typography className="completed-field" sx={{ fontSize: '16px' }}>{`${t(
                    'content.coach.completed',
                )}: ${dayjs.unix(session.completed_at).format(profile.dateTimeFormat)}`}</Typography>
            </Box>
        );
    } else if (session.last_sent) {
        datetimeField = (
            <Box sx={{ display: 'flex' }}>
                <Box sx={{ width: 28, display: 'flex', alignItems: 'center' }}>
                    <SendToMobileIcon fontSize="small" sx={{ color: 'primary.main' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography className="last-sent-field" sx={{ fontSize: '16px' }}>{`${t(
                        'content.coach.sort.last_sent',
                    )}: ${dayjs.unix(session.last_sent).format(profile.dateTimeFormat)}`}</Typography>
                    {session.last_delivery_status === 'failed' || session.last_delivery_status === 'undelivered' ? (
                        <Tooltip title={t('content.coach.undelivered')}>
                            <ErrorIcon color="error" sx={{ fontSize: 16, mb: 0.25, ml: 0.5 }} />
                        </Tooltip>
                    ) : null}
                </Box>
            </Box>
        );
    } else {
        datetimeField = (
            <Box sx={{ display: 'flex' }}>
                <Box sx={{ width: 28, display: 'flex', alignItems: 'center' }}>
                    <EventIcon fontSize="small" sx={{ color: 'primary.main' }} />
                </Box>
                <Typography className="last-updated-field" sx={{ fontSize: '16px' }}>{`${t(
                    'content.coach.last_updated',
                )}: ${dayjs.unix(session.last_updated).format(profile.dateTimeFormat)}`}</Typography>
            </Box>
        );
    }
    const bottomRow = (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {datetimeField}
            {session.auto_generated ? <AutoFixHighIcon fontSize="small" sx={{ color: palette.neutral[400] }} /> : null}
        </Box>
    );

    const assignee = !!session.assignee ? props.data.users.find((user) => user.user_id === session.assignee) : null;

    return (
        <ListItem
            data-testid={`session-list-item-${session.session_id}`}
            disableGutters
            disablePadding
            style={style}
            onContextMenu={(e: React.MouseEvent) => {
                if (props.data.canRemoveSession) {
                    handleContextMenu(e);
                }
            }}
            sx={{ width: '100%', height: '100px', borderBottom: `1px solid ${palette.gray[400]}`, direction: 'ltr' }}
        >
            <Menu
                open={contextMenu !== null}
                onClose={handleClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
                }
            >
                <MenuItem
                    id="remove-session-menu-item"
                    onClick={() => {
                        props.data.setSessionIdToDelete(session.session_id);
                        handleClose();
                    }}
                >
                    {t('content.coach.details.remove')}
                </MenuItem>
            </Menu>
            <ListItemButton
                id={`session-list-item-${session.driver_name?.toLocaleLowerCase().replaceAll(' ', '-')}`}
                data-testid="session-list-item"
                className={`session-${session.driver_name || 'unknown'}`}
                selected={
                    !!props.data.selectedSessionId
                        ? parseInt(props.data.selectedSessionId) === session.session_id
                        : false
                }
                sx={{ width: '100%', height: '125px', position: 'relative' }}
                onClick={() => history.push(`/coaching/${session.session_id}`)}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                py: 0.5,
                                maxWidth: '66%',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    width: '100%',
                                }}
                            >
                                <Box sx={{ width: 28, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                    <PersonIcon
                                        fontSize="small"
                                        sx={{ color: !!session.driver_name ? 'primary.main' : 'wellnessGray.main' }}
                                    />
                                </Box>
                                <Typography
                                    data-testid="list-item-driver-name"
                                    sx={{
                                        color: !!session.driver_name ? undefined : palette.neutral[700],
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {session.driver_name !== ''
                                        ? session.driver_name
                                        : t('content.fleet.unknown_driver')}
                                </Typography>
                                {session.auto_generated && !!session.sub_fleet_name ? (
                                    <Typography
                                        sx={{
                                            ml: 0.5,
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >{`(${session.sub_fleet_name})`}</Typography>
                                ) : null}
                            </Box>
                            <Box sx={{ display: 'flex', width: '100%' }}>
                                <Box sx={{ width: 28, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                    <SupervisorAccountIcon
                                        fontSize="small"
                                        sx={{ color: !!assignee ? 'primary.main' : 'wellnessGray.main' }}
                                    />
                                </Box>
                                <Typography
                                    data-testid="list-item-assignee"
                                    sx={{
                                        color: !!assignee ? undefined : 'wellnessGray.main',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {!!assignee ? assignee.name : t('content.coach.details.unassigned')}
                                </Typography>
                            </Box>
                            {eventsField}
                        </Box>
                        {!!session.behaviors?.length ? (
                            <Box sx={{ py: 0.5, width: '33%' }}>
                                <Typography sx={{ fontSize: 12, color: 'grayColor.main', fontWeight: 'bold' }}>
                                    {t('content.safety.coaching.behaviors')}
                                </Typography>
                                <Typography id={`behavior-${session.session_id}`} sx={{ fontSize: 14 }}>
                                    {t(BEHAVIOR_MAP[session.behaviors[0]])}
                                    <Typography
                                        sx={{ display: 'inline', color: 'grayColor.main' }}
                                        title={session.behaviors
                                            .slice(1)
                                            .map((b) => t(BEHAVIOR_MAP[b]))
                                            .join(', ')}
                                    >
                                        {session.behaviors.length > 1 ? ` (+${session.behaviors.length - 1})` : ''}
                                    </Typography>
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                    <Box>{bottomRow}</Box>
                </Box>
            </ListItemButton>
        </ListItem>
    );
};

export default SessionListRow;
